import React, { useState, useEffect } from 'react';
import {
    Button,
    Form,
    Input,
    Col,
    Row,
    DatePicker,
    Select,
    Card,
    Space,
    message,
    Switch
} from 'antd';
import { UserOutlined, BankOutlined, CalendarOutlined, LockOutlined, SafetyOutlined, GithubOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import * as adminactions from "store/actions/AuthActions";
import dayjs from 'dayjs';
import SetPassword from '../Auth/SetPassword';

const { Option } = Select;

const EmployeeForm = ({ instance, form, reload, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const organization = useSelector(state => state.auth.organization);
    const [showSetPassword, setShowSetPassword] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    useEffect(() => {
        loadInitialData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (instance) {
            const formData = {
                ...instance,
                date_of_joining: instance.date_of_joining ? dayjs(instance.date_of_joining) : null,
                last_working_day: instance.last_working_day ? dayjs(instance.last_working_day) : null,
                date_of_birth: instance.date_of_birth ? dayjs(instance.date_of_birth) : null,
                user_id: instance.user?.id,
                organization_id: organization, // Auto-set to current organization
                is_2fa_enabled: instance.user?.is_2fa_enabled || false,
                github_username: instance.github_username || '',
                gitlab_username: instance.gitlab_username || '',
            };
            form.setFieldsValue(formData);
        }
    }, [instance, form, organization]);

    const loadInitialData = async () => {
        try {
            // Load users
            const usersResponse = await adminactions.loadUsers(1, '');

            if (usersResponse?.results) {
                setUsers(usersResponse.results);
            }
        } catch (error) {
            message.error('Failed to load initial data');
            console.error('Error loading initial data:', error);
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            // Format dates for API and auto-include organization
            const formattedValues = {
                ...values,
                organization_id: organization, // Auto-include current organization
                date_of_joining: values.date_of_joining ? values.date_of_joining.format('YYYY-MM-DD') : null,
                last_working_day: values.last_working_day ? values.last_working_day.format('YYYY-MM-DD') : null,
                date_of_birth: values.date_of_birth ? values.date_of_birth.format('YYYY-MM-DD') : null,
            };

            // Remove undefined values - convert to null or remove them entirely
            const cleanedValues = Object.entries(formattedValues).reduce((acc, [key, value]) => {
                if (value !== undefined) {
                    acc[key] = value;
                }
                return acc;
            }, {});

            let response;
            if (instance) {
                response = await adminactions.updateEmployees(cleanedValues, instance.id);
                if (response && !response.message) {
                    // Update user's 2FA setting if changed
                    if (instance.user && values.is_2fa_enabled !== instance.user.is_2fa_enabled) {
                        await adminactions.updateUsers({
                            is_2fa_enabled: values.is_2fa_enabled
                        }, instance.user.id);
                    }
                    message.success('Employee updated successfully!');
                    reload && reload();
                    onCancel && onCancel();
                } else {
                    message.error(response?.message || 'Failed to update employee');
                }
            } else {
                response = await adminactions.addEmployees(cleanedValues);
                if (response && !response.message) {
                    // Update user's 2FA setting for new employee
                    if (values.user_id && values.is_2fa_enabled) {
                        await adminactions.updateUsers({
                            is_2fa_enabled: values.is_2fa_enabled
                        }, values.user_id);
                    }
                    message.success('Employee created successfully!');
                    form.resetFields();
                    reload && reload();
                    onCancel && onCancel();
                } else {
                    message.error(response?.message || 'Failed to create employee');
                }
            }
        } catch (error) {
            message.error('An error occurred while saving employee');
            console.error('Error saving employee:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSetPassword = (userId) => {
        const user = users.find(u => u.id === userId);
        if (user) {
            setSelectedUser(user);
            setShowSetPassword(true);
        }
    };

    const handlePasswordSetSuccess = () => {
        setShowSetPassword(false);
        setSelectedUser(null);
        message.success('Password set successfully!');
    };

    return (
        <div style={{ maxHeight: '80vh', overflowY: 'auto', paddingRight: 16 }}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                autoComplete="off"
                size="middle"
                scrollToFirstError
            >
                {/* Personal & Basic Information */}
                <Card 
                    title={<><UserOutlined /> Basic Information</>} 
                    size="small" 
                    style={{ marginBottom: 20 }}
                >
                    <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Employee ID"
                                name="employee_id"
                                rules={[{ required: true, message: 'Please enter employee ID!' }]}
                            >
                                <Input placeholder="Enter employee ID" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="User Account"
                                name="user_id"
                                rules={[{ required: true, message: 'Please select a user account!' }]}
                            >
                                <Select
                                    placeholder="Select user account"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    onChange={(value) => {
                                        form.setFieldsValue({ user_id: value });
                                        const selectedUser = users.find(u => u.id === value);
                                        if (selectedUser) {
                                            form.setFieldsValue({ 
                                                is_2fa_enabled: selectedUser.is_2fa_enabled || false 
                                            });
                                        }
                                    }}
                                >
                                    {users.map(user => (
                                        <Option key={user.id} value={user.id}>
                                            {user.first_name} {user.last_name} ({user.email})
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Date of Birth"
                                name="date_of_birth"
                            >
                                <DatePicker 
                                    style={{ width: '100%' }}
                                    placeholder="Select date of birth"
                                    format="YYYY-MM-DD"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Employment Information */}
                <Card 
                    title={<><CalendarOutlined /> Employment Details</>} 
                    size="small" 
                    style={{ marginBottom: 20 }}
                >
                    <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Job Title"
                                name="job_title"
                                rules={[{ required: true, message: 'Please enter job title!' }]}
                            >
                                <Input placeholder="Enter job title" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Department"
                                name="department"
                                rules={[{ required: true, message: 'Please enter department!' }]}
                            >
                                <Input placeholder="Enter department" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Employment Type"
                                name="employment_type"
                                rules={[{ required: true, message: 'Please select employment type!' }]}
                            >
                                <Select placeholder="Select employment type">
                                    <Option value="Full-time">Full-time</Option>
                                    <Option value="Part-time">Part-time</Option>
                                    <Option value="Contract">Contract</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Date of Joining"
                                name="date_of_joining"
                            >
                                <DatePicker 
                                    style={{ width: '100%' }}
                                    placeholder="Select joining date"
                                    format="YYYY-MM-DD"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Last Working Day"
                                name="last_working_day"
                            >
                                <DatePicker 
                                    style={{ width: '100%' }}
                                    placeholder="Select last working day"
                                    format="YYYY-MM-DD"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Supervisor"
                                name="supervisor"
                            >
                                <Select
                                    placeholder="Select supervisor (optional)"
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {/* You can populate this with other employees */}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Machine ID"
                                name="machine_id"
                                tooltip="Unique machine/device ID for attendance tracking"
                            >
                                <Input placeholder="Enter machine ID" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Consider Attendance for Payroll"
                                name="is_attendance_considered"
                                valuePropName="checked"
                                tooltip="When enabled, attendance will be factored into payroll calculations. Disable for employees with fixed salary regardless of attendance."
                            >
                                <Switch
                                    checkedChildren="Yes"
                                    unCheckedChildren="No"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Developer Profiles */}
                <Card
                    title={<><GithubOutlined /> Developer Profiles</>}
                    size="small"
                    style={{ marginBottom: 20 }}
                >
                    <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="GitHub Username"
                                name="github_username"
                            >
                                <Input placeholder="Enter GitHub username" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="GitLab Username"
                                name="gitlab_username"
                            >
                                <Input placeholder="Enter GitLab username" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Financial Information */}
                <Card
                    title={<><BankOutlined /> Financial Information</>}
                    size="small"
                    style={{ marginBottom: 20 }}
                >
                    <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="PAN Card"
                                name="pan_card"
                                rules={[
                                    { pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: 'Invalid PAN format!' }
                                ]}
                            >
                                <Input 
                                    placeholder="Enter PAN card number"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Bank Account Number"
                                name="bank_account_number"
                            >
                                <Input placeholder="Enter bank account number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Bank Name"
                                name="bank_name"
                            >
                                <Input placeholder="Enter bank name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Bank IFSC Code"
                                name="bank_ifsc"
                            >
                                <Input 
                                    placeholder="Enter IFSC code"
                                    style={{ textTransform: 'uppercase' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Bank Branch"
                                name="bank_branch"
                            >
                                <Input placeholder="Enter bank branch" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Security Information */}
                <Card 
                    title={<><SafetyOutlined /> Security Settings</>} 
                    size="small" 
                    style={{ marginBottom: 20 }}
                >
                    <Row gutter={[16, 8]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                label="Two-Factor Authentication"
                                name="is_2fa_enabled"
                                valuePropName="checked"
                            >
                                <Switch 
                                    checkedChildren="Enabled" 
                                    unCheckedChildren="Disabled"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item label="Password Management">
                                <Button
                                    icon={<LockOutlined />}
                                    onClick={() => {
                                        const userId = form.getFieldValue('user_id');
                                        if (userId) {
                                            handleSetPassword(userId);
                                        } else {
                                            message.warning('Please select a user account first');
                                        }
                                    }}
                                    disabled={!form.getFieldValue('user_id')}
                                >
                                    Set Password
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>
                {/* Action Buttons */}
                <Row style={{ marginTop: 20, marginBottom: 20 }}>
                    <Col span={24}>
                        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                            <Space>
                                <Button onClick={onCancel} size="large">
                                    Cancel
                                </Button>
                                <Button 
                                    type="primary" 
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                >
                                    {instance ? 'Update Employee' : 'Create Employee'}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            
            <SetPassword
                visible={showSetPassword}
                onCancel={() => setShowSetPassword(false)}
                onSuccess={handlePasswordSetSuccess}
                user={selectedUser}
            />
        </div>
    );
};

export default EmployeeForm;